// Feature 012: pure helpers for showing variant groups on the calendar boards.
import {VariantGroupType} from "../../redux/Content/VariantGroup/types";
import {PeerActivityModeCode} from "../../redux/Content/PeerActivityMode/types";
import {connectorHandlerCode} from "../../constants/ConnectorHandler";

export type VariantGroupCardState = 'open' | 'overdue' | 'decided' | 'cancelled';

export function groupDate(group: VariantGroupType): Date | null {
    const d = new Date(group.publishAt);
    return isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** The groups whose slot is on this day, earliest first. A group shows once, at its own time. */
export function groupsForDay(groups: VariantGroupType[] | undefined, day: Date): VariantGroupType[] {
    return (groups || [])
        .filter((g) => {
            const d = groupDate(g);
            return d !== null && sameDay(d, day);
        })
        .sort((a, b) => groupDate(a)!.getTime() - groupDate(b)!.getTime());
}

export function cardState(group: VariantGroupType): VariantGroupCardState {
    if (group.status === 'open' && group.overdue) {
        return 'overdue';
    }
    return group.status;
}

/** "2 of 3 channels picked" counts, for the card and the modal header. */
export function pickProgress(group: VariantGroupType): { picked: number; total: number } {
    const live = group.targets.filter((t) => t.state !== 'dropped');
    return {picked: live.filter((t) => t.state === 'assigned').length, total: live.length};
}

/** The kinds a group can be, in the order the calendar's own "New …" buttons use. */
export const GROUP_MODES: string[] = [PeerActivityModeCode.ACTIVITY, PeerActivityModeCode.SHORTS, PeerActivityModeCode.STORY, PeerActivityModeCode.BIO];

/** Reels and stories are a photo or video first: a version without media cannot be picked. */
export function modeNeedsMedia(mode: string | undefined): boolean {
    return mode === PeerActivityModeCode.SHORTS || mode === PeerActivityModeCode.STORY;
}

/** The kinds a channel takes, by the same flags the post editor uses when it creates posts per channel. */
export function channelModes(channel: any): string[] {
    const handler = channel?.connector?.handler;
    if (!handler || (handler.code === connectorHandlerCode.CONNECTOR_HANDLER_TELEGRAM_BOT && channel.type === 'personal')) {
        return [];
    }
    return [
        handler.canAddPost === true && PeerActivityModeCode.ACTIVITY,
        handler.canAddShort === true && PeerActivityModeCode.SHORTS,
        handler.canAddStory === true && PeerActivityModeCode.STORY,
        handler.canChangeBio === true && PeerActivityModeCode.BIO,
    ].filter(Boolean) as string[];
}

/** Telegram's limit for a caption under a photo or video, whatever the post limit says. */
const TELEGRAM_CAPTION = 1024;

/**
 * A network's text limit for a kind of post, from the connector data params the post editor uses
 * (code POST, which the editor renames TEXT in the store), with the same Telegram caption rule
 * as the cabinet's PostLengthLimit. Null when the network sets none.
 */
export function textLimit(params: any[] | undefined, channel: {handlerUuid?: string; network: string}, mode: string, hasMedia: boolean): number | null {
    const param = (params || []).find((p: any) =>
        (p?.code === 'POST' || p?.code === 'TEXT')
        && p?.handler?.uuid === channel.handlerUuid
        && p?.mode?.code === mode
        && p?.params?.maxLength > 0);
    const limit: number | null = param ? param.params.maxLength : null;
    const telegram = channel.network === connectorHandlerCode.CONNECTOR_HANDLER_TELEGRAM || channel.network === connectorHandlerCode.CONNECTOR_HANDLER_TELEGRAM_BOT;
    if (hasMedia && mode === PeerActivityModeCode.ACTIVITY && telegram) {
        return limit === null ? TELEGRAM_CAPTION : Math.min(limit, TELEGRAM_CAPTION);
    }
    return limit;
}

/** Characters of text plus tags, counted as the cabinet counts them (code points). */
export function measureText(text: string | null | undefined, tags: string | null | undefined): number {
    return Array.from(text || '').length + Array.from(tags || '').length;
}
